import React, { createContext, useContext, useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, onIdTokenChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

/* ── Impersonation persistida ──────────────────────────────────────────────
   Hasta 2026-08-27 la impersonation era SOLO estado de React: un F5 la
   perdía. En `/dashboard` eso apenas molestaba; en `/compass2` es peligroso,
   porque `CompassPilotGate` deja pasar a cualquier admin y la pantalla se
   convertía en el Compass DEL ADMIN sin avisar — incluida la apertura del día
   (`openGuiaDayTurn`), que se dispara sola al montar "Hoy" y escribe en la
   cuenta de quien esté mirando.

   POR QUÉ `sessionStorage` Y NO `localStorage`: una vista sobre la cuenta de
   otra persona tiene que morir al cerrar la pestaña. `localStorage`
   sobreviviría días.

   EL STORAGE NO ES UNA CREDENCIAL. Cualquiera puede escribir esta clave a
   mano desde la consola del navegador. Por eso la rehidratación (ver
   `onIdTokenChanged` más abajo) exige el claim `admin` del ID token ANTES de
   mirar el storage, y si no está lo borra. La única autoridad es el token; el
   storage solo dice "a quién estaba mirando". ── */

const IMPERSONATION_STORAGE_KEY = 'sfs.impersonation';

/** `'readonly'` salvo que se pida `'full'` explícitamente — seguro por defecto. */
function normalizeImpersonationMode(mode) {
  return mode === 'full' ? 'full' : 'readonly';
}

function readImpersonationStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.studentId || typeof parsed.studentId !== 'string') return null;
    return { ...parsed, mode: normalizeImpersonationMode(parsed.mode) };
  } catch (e) {
    // JSON corrupto o Safari en modo privado. Sin dato no se impersona, que
    // es exactamente el lado seguro del error.
    return null;
  }
}

function writeImpersonationStorage(entry) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(entry));
  } catch (e) { /* sin persistencia se sigue funcionando en memoria */ }
}

function clearImpersonationStorage() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch (e) { /* idem */ }
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [studentData, setStudentData] = useState(null); // State for student data
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [isFlashPartner, setIsFlashPartner] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dashboard V4 preference flag
  const [useDashboardV4, setUseDashboardV4Local] = useState(false);
  
  // Impersonation state
  const [impersonatedStudentId, setImpersonatedStudentId] = useState(null);
  const [impersonatedStudentData, setImpersonatedStudentData] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);
  // `'readonly'` | `'full'` | null (sin impersonation). Ver el bloque de
  // cabecera: el default de `startImpersonation` es `'readonly'`.
  const [impersonationMode, setImpersonationMode] = useState(null);
  /* Había una vista abierta en el storage y NO se pudo retomar (el documento
     del estudiante ya no está, o Firestore falló). Sin esto, la pantalla
     volvería a ser la del admin en silencio — que es exactamente el problema
     que esta iniciativa vino a cerrar, solo que por el camino del error en vez
     del camino del refresh. Lo lee AdminViewBanner.js para avisar. */
  const [impersonationRestoreFailed, setImpersonationRestoreFailed] = useState(false);

  // Effective user - returns impersonated user if active, otherwise real user
  const effectiveUserId = impersonatedStudentId || currentUser?.uid;
  const effectiveStudentData = impersonatedStudentData || studentData;
  // El booleano que consume el guard de escritura (readOnlyGuard.js). Exige
  // impersonation ACTIVA: un admin mirando su propia cuenta no es read-only.
  const isReadOnlyView = !!impersonatedStudentId && impersonationMode === 'readonly';

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        // Fetch student data from Firestore
        const studentRef = doc(db, 'students', user.uid);
        const studentSnap = await getDoc(studentRef);
        if (studentSnap.exists()) {
          const data = studentSnap.data();
          setStudentData(data);
          // Load dashboard V4 preference
          setUseDashboardV4Local(data.useDashboardV4 ?? false);
        } else {
          setStudentData(null); // User authenticated but no profile in DB
          setUseDashboardV4Local(false);
        }

        // Get custom claims from ID token
        const idTokenResult = await user.getIdTokenResult();
        const userIsAdmin = !!idTokenResult.claims.admin;
        setIsAdmin(userIsAdmin);
        setIsTutor(!!idTokenResult.claims.tutor);
        setIsFlashPartner(!!idTokenResult.claims.flash_partner);

        /* Rehidratación de la impersonation (2026-08-27). Corre ACÁ, dentro
           del listener del token y antes de `setLoading(false)`, por dos
           motivos que no son de estilo:

           1. El claim `admin` recién existe en este punto. Leer el storage
              antes sería confiar en el storage, que es justamente lo que no
              se puede hacer — un no-admin que fabrique la clave a mano cae en
              el `else` de abajo y se le borra.
           2. El provider renderiza `{!loading && children}`, así que resolver
              esto antes de apagar `loading` garantiza que ninguna pantalla se
              monte con la identidad equivocada. Sin eso, `/compass2` haría un
              primer render con el uid del admin y `openGuiaDayTurn` (que se
              dispara solo al montar "Hoy") escribiría en la cuenta del admin
              antes de que la rehidratación llegara. */
        const savedImpersonation = readImpersonationStorage();
        if (savedImpersonation && userIsAdmin) {
          try {
            const savedSnap = await getDoc(doc(db, 'students', savedImpersonation.studentId));
            if (savedSnap.exists()) {
              setImpersonatedStudentId(savedImpersonation.studentId);
              setImpersonatedStudentData(savedSnap.data());
              setImpersonationMode(savedImpersonation.mode);
              setImpersonationRestoreFailed(false);
            } else {
              clearImpersonationStorage();
              setImpersonationRestoreFailed(true);
            }
          } catch (error) {
            console.error('Error rehydrating impersonation:', error);
            clearImpersonationStorage();
            setImpersonationRestoreFailed(true);
          }
        } else if (savedImpersonation) {
          // Había una entrada y quien está autenticado NO es admin: se borra.
          clearImpersonationStorage();
        }
      } else {
        setCurrentUser(null);
        setStudentData(null);
        setIsAdmin(false);
        setIsTutor(false);
        setIsFlashPartner(false);
        // Cerrar sesión también cierra cualquier vista sobre otra cuenta.
        setImpersonatedStudentId(null);
        setImpersonatedStudentData(null);
        setImpersonationMode(null);
        clearImpersonationStorage();
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const signup = async (email, password, firstName, lastName) => {
    // Just create the auth user - let the calling component create the Firestore document
    // This prevents duplicate document creation attempts
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;
    const userDoc = await getDoc(doc(db, "students", user.uid));
    const isNewUser = !userDoc.exists();

    if (isNewUser) {
      const firstName = user.displayName?.split(" ")[0] || user.email?.split('@')[0] || 'Student';
      const lastName = user.displayName?.split(" ").slice(1).join(' ') || '';

      await setDoc(doc(db, "students", user.uid), {
        fullName: user.displayName || user.email,
        firstName,
        lastName,
        email: user.email,
        role: "student",
        plan: "new",
        folderLink: "https://zoomdocs.com/your-default-folder",
        createdAt: new Date(),
        totalHours: 0,
        hoursPerWeek: 0,
        signupMethod: 'google',
        source: "google-login-signup",
        sessions_balance: { guided: 0, freeChats: 0 }
      });
    }

    // Return user with isNewUser flag
    return { user, isNewUser };
  };

  const signupWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential;
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // Redirigir al index después del logout
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  /**
   * Abre la vista sobre la cuenta de un estudiante.
   *
   * @param {string} studentId
   * @param {{ mode?: 'readonly'|'full' }} [options] — `'readonly'` por defecto.
   *   `'full'` es el comportamiento histórico (el admin puede escribir) y hay
   *   que pedirlo explícitamente: el flujo viejo de soporte hacia `/dashboard`
   *   (InfoTab.js) lo hace, la vista de Compass 2.0 no.
   * @returns {Promise<boolean>} `true` si la vista quedó abierta. Antes esta
   *   función no devolvía nada y fallaba solo por consola, así que quien la
   *   llamaba navegaba igual y el admin aterrizaba en la pantalla del
   *   estudiante... siendo él mismo. Los llamadores viejos que ignoran el
   *   valor siguen funcionando igual que antes.
   */
  const startImpersonation = async (studentId, { mode } = {}) => {
    if (!isAdmin) {
      console.error('Only admins can impersonate students');
      return false;
    }

    const resolvedMode = normalizeImpersonationMode(mode);

    // Save current URL to return later. Incluye el query string: volver de
    // `/compass2?section=progreso` a `/compass2` a secas perdía la sección.
    if (typeof window !== 'undefined') {
      setReturnUrl(window.location.pathname + window.location.search);
    }

    try {
      const studentRef = doc(db, 'students', studentId);
      const studentSnap = await getDoc(studentRef);

      if (studentSnap.exists()) {
        setImpersonatedStudentId(studentId);
        setImpersonatedStudentData(studentSnap.data());
        setImpersonationMode(resolvedMode);
        writeImpersonationStorage({
          studentId,
          mode: resolvedMode,
          // Informativo, para poder mirar en consola desde cuándo está
          // abierta la vista. No es un vencimiento: nada lo lee todavía.
          startedAt: new Date().toISOString(),
        });
        console.log(`Started impersonation for student: ${studentId} (${resolvedMode})`);
        // La bitácora es best-effort y va en su propio try: si falla, la vista
        // igual se abre. Sin esto, un error de permisos acá tumbaba la feature
        // entera — el `addDoc` tiraba, el catch de afuera devolvía false y el
        // botón no navegaba, aunque la impersonation ya estuviera puesta.
        // `stopImpersonation` ya lo hacía así; esta rama estaba desalineada.
        try {
          await addDoc(collection(db, 'admin_logs'), {
            action: 'impersonation_start',
            targetUserId: studentId,
            mode: resolvedMode,
            performedBy: currentUser.uid,
            performedByEmail: currentUser.email,
            timestamp: serverTimestamp(),
          });
        } catch (e) {
          console.error('Error logging impersonation start:', e);
        }
        return true;
      }
      console.error('Student not found:', studentId);
      return false;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return false;
    }
  };

  const stopImpersonation = async () => {
    const savedReturnUrl = returnUrl;
    const stoppedStudentId = impersonatedStudentId;
    if (stoppedStudentId && currentUser) {
      try {
        await addDoc(collection(db, 'admin_logs'), {
          action: 'impersonation_stop',
          targetUserId: stoppedStudentId,
          mode: impersonationMode,
          performedBy: currentUser.uid,
          performedByEmail: currentUser.email,
          timestamp: serverTimestamp(),
        });
      } catch (e) { console.error('Error logging impersonation stop:', e); }
    }
    setImpersonatedStudentId(null);
    setImpersonatedStudentData(null);
    setImpersonationMode(null);
    setImpersonationRestoreFailed(false);
    setReturnUrl(null);
    // Lo primero que hay que soltar: sin esto, un F5 después de salir
    // reabriría la vista.
    clearImpersonationStorage();
    console.log('Stopped impersonation');
    return savedReturnUrl;
  };

  // Refresh student data from Firestore (e.g. after assessment completion)
  const refreshStudentData = async () => {
    if (!currentUser) return;
    try {
      const studentRef = doc(db, 'students', currentUser.uid);
      const studentSnap = await getDoc(studentRef);
      if (studentSnap.exists()) {
        setStudentData(studentSnap.data());
      }
    } catch (error) {
      console.error('Error refreshing student data:', error);
    }
  };

  // Dashboard V4 toggle function - persists preference to Firestore
  const setUseDashboardV4 = async (value) => {
    if (!currentUser) return;

    try {
      const studentRef = doc(db, 'students', currentUser.uid);
      await updateDoc(studentRef, { useDashboardV4: value });
      setUseDashboardV4Local(value);
      console.log('Dashboard preference updated:', value ? 'V4' : 'Classic');
    } catch (error) {
      console.error('Error updating dashboard preference:', error);
    }
  };

  // School program variant selection — persists to Firestore and updates local studentData
  const selectSchoolProgram = async (programId) => {
    if (!currentUser) return;
    try {
      const studentRef = doc(db, 'students', currentUser.uid);
      await updateDoc(studentRef, { selectedSchoolProgram: programId });
      setStudentData((prev) => ({ ...prev, selectedSchoolProgram: programId }));
    } catch (error) {
      console.error('Error updating selected school program:', error);
    }
  };

  const value = {
    currentUser,
    studentData, // Expose student data in context
    setStudentData, // Expose setter for student data
    isAdmin,
    isTutor,
    isFlashPartner,
    loading,
    login,
    signup,
    loginWithGoogle,
    signupWithGoogle,
    logout,
    // Dashboard version preference
    useDashboardV4,
    setUseDashboardV4,
    // School program variant
    selectSchoolProgram,
    // Impersonation
    impersonatedStudentId,
    impersonatedStudentData,
    impersonationMode,
    isReadOnlyView,
    impersonationRestoreFailed,
    effectiveUserId,
    effectiveStudentData,
    startImpersonation,
    stopImpersonation,
    refreshStudentData,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
